import React, { useState, useEffect, useRef, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUntypedTranslation } from '../../hooks/useUntypedTranslation';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Link as MuiLink,
  useMediaQuery,
  useTheme,
  InputAdornment,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  verifyResetCode,
  completePasswordReset,
  isValidPassword,
} from '../../services/authService';

interface PasswordResetFormProps {
  oobCode: string;
}

/**
 * PasswordResetForm Component
 * Handles the complete password reset flow with oobCode validation
 * Implements password validation and security best practices
 */
const PasswordResetForm: React.FC<PasswordResetFormProps> = ({ oobCode }) => {
  const navigate = useNavigate();
  const { t } = useUntypedTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Ref to ensure code verification only happens once
  const hasVerifiedRef = useRef(false);

  // Component state
  const [email, setEmail] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [codeValid, setCodeValid] = useState(false);

  /**
   * Verifies the reset code on component mount (only once)
   * Uses a ref to prevent re-verification after password reset success
   */
  useEffect(() => {
    // Prevent re-verification - critical for avoiding the "invalid code" error after successful reset
    if (hasVerifiedRef.current) {
      return;
    }

    const verifyCode = async () => {
      if (!oobCode) {
        setError(t('auth.resetPassword.invalidCode'));
        setVerifying(false);
        return;
      }

      try {
        console.log('🔍 Verifying password reset code...');
        const userEmail = await verifyResetCode(oobCode);

        setEmail(userEmail);
        setCodeValid(true);
        hasVerifiedRef.current = true; // Mark as verified to prevent re-verification
        console.log('✅ Code verified successfully for:', userEmail);

        // Clean URL immediately after successful verification
        // Removes sensitive oobCode from browser history while keeping it in component state
        if (window.history.replaceState) {
          const cleanUrl = `${window.location.origin}${window.location.pathname}`;
          window.history.replaceState({}, document.title, cleanUrl);
          console.log('🧹 URL cleaned - sensitive parameters removed from browser history');
        }
      } catch (error: any) {
        console.error('❌ Code verification failed:', error);

        if (error.message === 'INVALID_CODE' || error.message === 'EXPIRED_CODE') {
          setError(t('auth.resetPassword.invalidCode'));
        } else {
          setError(t('auth.errors.generic'));
        }
        setCodeValid(false);
        hasVerifiedRef.current = true; // Mark as attempted even on failure
      } finally {
        setVerifying(false);
      }
    };

    verifyCode();
  }, [oobCode, t]);

  /**
   * Validates password requirements
   */
  const validatePasswords = (): string | null => {
    // Check if passwords match
    if (newPassword !== confirmPassword) {
      return t('auth.resetPassword.passwordMismatch');
    }

    // Check password strength
    if (!isValidPassword(newPassword)) {
      return t('auth.resetPassword.weakPassword');
    }

    return null;
  };

  /**
   * Handles form submission for password reset
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Reset error state
    setError(null);

    // Validate passwords
    const validationError = validatePasswords();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      // Complete password reset
      await completePasswordReset(oobCode, newPassword);

      console.log('✅ Password reset completed successfully');
      setSuccess(true);

      // Redirect to sign-in after 2 seconds
      setTimeout(() => {
        navigate('/signin');
      }, 2000);
    } catch (error: any) {
      console.error('❌ Password reset failed:', error);

      // Handle different error types
      if (error.message === 'WEAK_PASSWORD') {
        setError(t('auth.resetPassword.weakPassword'));
      } else if (error.message === 'INVALID_CODE' || error.message === 'EXPIRED_CODE') {
        setError(t('auth.resetPassword.invalidCode'));
      } else {
        setError(t('auth.errors.generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Navigates back to sign-in page
   */
  const handleBackToSignIn = () => {
    navigate('/signin');
  };

  /**
   * Toggles password visibility
   */
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  /**
   * Toggles confirm password visibility
   */
  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Loading state while verifying code
  if (verifying) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 120px)',
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  // Invalid code state
  if (!codeValid) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: isMobile ? 3 : 4,
          maxWidth: 500,
          width: '100%',
          textAlign: 'center',
          borderRadius: { xs: 2, sm: 3 },
          backgroundColor: 'background.paper',
        }}
      >
        <Typography
          variant="h4"
          sx={{
            mb: 2,
            color: 'primary.main',
          }}
        >
          {t('auth.resetPassword.title')}
        </Typography>

        <Alert severity="error" sx={{ mb: 2.5 }}>
          {error}
        </Alert>

        <MuiLink
          component="button"
          type="button"
          variant="body2"
          onClick={handleBackToSignIn}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            textDecoration: 'none',
            color: 'primary.main',
            cursor: 'pointer',
            fontSize: '0.875rem',
            '&:hover': {
              textDecoration: 'underline',
            },
            border: 'none',
            background: 'none',
            padding: 0,
            font: 'inherit',
          }}
        >
          <ArrowBackIcon fontSize="small" />
          {t('auth.resetPassword.backToSignIn')}
        </MuiLink>
      </Paper>
    );
  }

  // Main password reset form
  return (
    <Paper
      elevation={3}
      sx={{
        p: isMobile ? 2 : 4,
        maxWidth: 500,
        width: '100%',
        textAlign: 'center',
        borderRadius: { xs: 2, sm: 3 },
        backgroundColor: 'background.paper',
      }}
    >
      {/* Header */}
      <Typography
        variant="h4"
        sx={{
          mb: 2,
          color: 'primary.main',
        }}
      >
        {t('auth.resetPassword.title')}
      </Typography>

      {/* Email Display */}
      {email && (
        <Typography
          variant="body2"
          sx={{
            mb: 3,
            color: 'text.secondary',
          }}
        >
          {email}
        </Typography>
      )}

      {/* Success Message */}
      {success && (
        <Alert
          severity="success"
          sx={{
            mb: 2.5,
            textAlign: 'left',
          }}
        >
          {t('auth.resetPassword.successMessage')}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2.5,
            textAlign: 'left',
          }}
        >
          {error}
        </Alert>
      )}

      {/* Form */}
      {!success && (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mb: 3,
          }}
        >
          <TextField
            type={showPassword ? 'text' : 'password'}
            label={t('auth.resetPassword.newPasswordLabel')}
            variant="outlined"
            required
            fullWidth
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            disabled={loading}
            autoFocus
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={togglePasswordVisibility} edge="end" tabIndex={-1}>
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            type={showConfirmPassword ? 'text' : 'password'}
            label={t('auth.resetPassword.confirmPasswordLabel')}
            variant="outlined"
            required
            fullWidth
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            disabled={loading}
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={toggleConfirmPasswordVisibility} edge="end" tabIndex={-1}>
                    {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button variant="contained" type="submit" fullWidth disabled={loading}>
            {loading ? (
              <CircularProgress
                size={24}
                sx={{
                  color: 'white',
                }}
              />
            ) : (
              t('auth.resetPassword.submitButton')
            )}
          </Button>
        </Box>
      )}

      {/* Back to Sign In Link */}
      {!success && (
        <MuiLink
          component="button"
          type="button"
          variant="body2"
          onClick={handleBackToSignIn}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            textDecoration: 'none',
            color: 'primary.main',
            cursor: 'pointer',
            fontSize: '0.875rem',
            '&:hover': {
              textDecoration: 'underline',
            },
            border: 'none',
            background: 'none',
            padding: 0,
            font: 'inherit',
          }}
        >
          <ArrowBackIcon fontSize="small" />
          {t('auth.resetPassword.backToSignIn')}
        </MuiLink>
      )}
    </Paper>
  );
};

export default PasswordResetForm;
